import React, {ReactElement, useRef} from 'react';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {getAnswerColors} from '../common/utils';
import Button from '../../../../../incase-domain/components/button/Button';
import AnswerComment from './AnswerComment';

export enum AnswerTypes {
    SELECTED = 'marked',
    CORRECT = 'correct',
    INCORRECT = 'incorrect',
    DEBATABLE = 'debatable',
    NONE = 'none',
}

interface QuestionAnswerProps {
    answer: string;
    answerType: AnswerTypes;
    comment?: string;
    leadingIcon?: string;
    onClick?: () => void;
    correctColor?: string;
    incorrectColor?: string;
    debatableColor?: string;
    writeAnswerIndices?: boolean;
    index?: number;
    draggable?: boolean;
    styles?: React.CSSProperties;
    disabled?: boolean;
    device?: DeviceType;
    showComment?: boolean;
    setShowComment: (show: boolean) => void;
}

function QuestionAnswer({
    answer,
    answerType,
    comment,
    leadingIcon,
    onClick,
    correctColor,
    incorrectColor,
    debatableColor,
    draggable = false,
    writeAnswerIndices,
    index,
    styles,
    disabled = false,
    device = DeviceType.DESKTOP,
    showComment = false,
    setShowComment,
}: QuestionAnswerProps): ReactElement {
    const questionAnswerRef = useRef<HTMLDivElement>(null);

    const {getBtnAnswerColor, getCustomAnswerColor, getCustomAnswerColorWithAlpha} = getAnswerColors(
        answerType,
        correctColor,
        incorrectColor,
        debatableColor
    );

    function getAnswerText(): string {
        if (index === undefined) return answer;
        return writeAnswerIndices ? `${String.fromCharCode('A'.charCodeAt(0) + index)}: ${answer}` : answer;
    }

    return (
        <div ref={questionAnswerRef} className={`c-questionElement__answer ${showComment ? 'has-comment-open' : ''}`}>
            <Button
                variant="soft"
                color={getBtnAnswerColor()}
                customColor={getCustomAnswerColorWithAlpha(0.35)}
                customIconColor={getCustomAnswerColor()}
                size="md"
                leading={leadingIcon}
                align="left"
                onClick={() => {
                    onClick?.();
                }}
                dynamicSize
                disablePointerEvents={draggable}
                disabled={disabled}
            >
                <div style={styles}>{getAnswerText()}</div>
            </Button>

            {comment && (
                <AnswerComment
                    answer={getAnswerText()}
                    comment={comment}
                    showComment={showComment}
                    setShowComment={setShowComment}
                    anchorRef={questionAnswerRef}
                    device={device}
                />
            )}
        </div>
    );
}

export default QuestionAnswer;
